type EnvKey =
  | 'AMPLITUDE_KEY'
  | 'AMPLITUDE_PROXY_DOMAIN_NAME'
  | 'CUSTOM_STYLES_BUCKET_URL'
  | 'STORE_HOST_NAME'
  | 'BASE_API_URL'
  | 'IS_SUPERSERVE_UI'
  | 'ALL_FEATURE_FLAGS_ENABLED'
  | 'SUPERSERVE_BACKEND_URL'
  | 'GROWTHBOOK_CLIENT_KEY'
  | 'GROWTHBOOK_ENABLE_DEV_MODE'
  | 'SUPERBAR_CDN_URL';

/**
 * Get a Next.js NEXT_PUBLIC_* env var using static property access.
 *
 * Next.js replaces `process.env.NEXT_PUBLIC_*` at compile time via DefinePlugin.
 * Dynamic access like `process.env['NEXT_PUBLIC_' + key]` is NOT replaced.
 * Each key must be a static property access for the replacement to work.
 * Wrapped in try/catch for Vite builds where `process` may not exist.
 */
function getNextPublicEnv(key: EnvKey): string | undefined {
  try {
    switch (key) {
      case 'AMPLITUDE_KEY':
        return process.env.NEXT_PUBLIC_AMPLITUDE_KEY;
      case 'AMPLITUDE_PROXY_DOMAIN_NAME':
        return process.env.NEXT_PUBLIC_AMPLITUDE_PROXY_DOMAIN_NAME;
      case 'CUSTOM_STYLES_BUCKET_URL':
        return process.env.NEXT_PUBLIC_CUSTOM_STYLES_BUCKET_URL;
      case 'STORE_HOST_NAME':
        return process.env.NEXT_PUBLIC_STORE_HOST_NAME;
      case 'BASE_API_URL':
        return process.env.NEXT_PUBLIC_BASE_API_URL;
      case 'IS_SUPERSERVE_UI':
        return process.env.NEXT_PUBLIC_IS_SUPERSERVE_UI;
      case 'ALL_FEATURE_FLAGS_ENABLED':
        return process.env.NEXT_PUBLIC_ALL_FEATURE_FLAGS_ENABLED;
      case 'SUPERSERVE_BACKEND_URL':
        return process.env.NEXT_PUBLIC_SUPERSERVE_BACKEND_URL;
      case 'GROWTHBOOK_CLIENT_KEY':
        return process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY;
      case 'GROWTHBOOK_ENABLE_DEV_MODE':
        return process.env.NEXT_PUBLIC_GROWTHBOOK_ENABLE_DEV_MODE;
      case 'SUPERBAR_CDN_URL':
        return process.env.NEXT_PUBLIC_SUPER_CDN_URL;
    }
  } catch {
    return undefined;
  }
}

/**
 * Get a Vite VITE_* env var via import.meta.env, with process.env fallback
 * for Vitest compatibility (vi.stubEnv sets process.env but not import.meta.env).
 */
function getViteEnv(key: EnvKey): string | undefined {
  try {
    if (
      typeof import.meta !== 'undefined' &&
      (import.meta as any).env !== undefined
    ) {
      const value = (import.meta as any).env[`VITE_${key}`];
      if (value !== undefined) return value;
    }
  } catch {
    /* not in a Vite environment */
  }

  try {
    if (typeof process !== 'undefined' && process.env) {
      return process.env[`VITE_${key}`];
    }
  } catch {
    /* process not available */
  }
  return undefined;
}

/**
 * Utility to get environment variables that works in both Vite and Next.js
 *
 * In Next.js: DefinePlugin statically replaces process.env.NEXT_PUBLIC_* at compile time.
 * In Vite: import.meta.env.VITE_* is available at runtime.
 *
 * Tries Next.js static replacements first, then falls back to Vite.
 * No EMBED_BUILD_SYSTEM flag needed - both paths are safe in either environment.
 */
export function getEnv(key: EnvKey): string | undefined {
  return getNextPublicEnv(key) ?? getViteEnv(key);
}

/**
 * Get environment variable as boolean
 * Returns true if value is exactly 'true', false otherwise
 */
export function getEnvBoolean(key: EnvKey): boolean {
  return getEnv(key) === 'true';
}
